import { useApi } from "@core-ui/hooks/useApi";
import { usePlatform } from "@core-ui/hooks/usePlatform";
import { formatBytes } from "@core-ui/utils/formatBytes";
import styled from "@emotion/styled";
import type { RemoteProgress, RemoteProgressPercentage } from "@ext/git/core/GitCommands/model/GitCommandsModel";
import t from "@ext/localization/locale/translate";
import { IconButton } from "@ui-kit/Button";
import { Divider } from "@ui-kit/Divider";
import { Progress } from "@ui-kit/Progress";
import { Tooltip, TooltipContent, TooltipTrigger } from "@ui-kit/Tooltip";
import { useCallback } from "react";

const collectProgressInfo = (data: RemoteProgress) => {
	if (!data) return "";

	if (data.type === "chunkedTransfer") {
		if (data.data.transfer.type === "indexingDeltas")
			return t("git.clone.indexing-deltas")
				.replace("{indexed}", data.data.transfer.data.indexed)
				.replace("{total}", data.data.transfer.data.total);

		if (data.data.transfer.type === "receivingObjects")
			return t("git.clone.receiving-objects")
				.replace("{received}", data.data.transfer.data.received)
				.replace("{indexed}", data.data.transfer.data.indexed)
				.replace("{total}", data.data.transfer.data.total);
	}

	if (data.type === "checkout")
		return t("git.clone.checkout")
			.replace("{checkouted}", data.data.checkouted)
			.replace("{total}", data.data.total);

	if (data.type == "sideband") return data.data.remoteText;

	if (data.type === "lfs")
		return t("git.clone.lfs")
			.replace("{handledObjects}", data.data.objectsHandled)
			.replace("{totalObjects}", data.data.totalObjects)
			.replace("{bytesHandled}", formatBytes(data.data.bytesHandled))
			.replace("{totalBytes}", formatBytes(data.data.totalBytes));
};

const formatSpeed = (data: RemoteProgress): string => {
	if (!data) return "";
	if (data.type !== "chunkedTransfer" && data.type !== "download" && data.type !== "lfs") return "";

	const perSecond = data.data.downloadSpeedBytes;

	if (isNaN(perSecond) || perSecond <= 0) return "";
	if (perSecond >= 1024 * 1024) return t("git.clone.etc.mbs").replace("{mbs}", (perSecond / 1024 / 1024).toFixed(2));
	if (perSecond >= 1024) return t("git.clone.etc.kbs").replace("{kbs}", (perSecond / 1024).toFixed(2));
	return t("git.clone.etc.bs").replace("{bs}", perSecond.toFixed(2));
};

const formatCollectedBytes = (data: RemoteProgress): string => {
	if (!data) return "";
	if (data.type !== "chunkedTransfer" && data.type !== "download" && data.type !== "lfs") return "";

	if (data.type === "lfs") return formatBytes(data.data.bytesHandled);
	return formatBytes(data.data.bytes);
};

const resolveLabelText = (data: RemoteProgress, isBrowser: boolean) => {
	if (!data?.type) return t("git.clone.progress.wait");

	if (data.type === "queue") return t("git.clone.progress.queue");
	if (data.type === "started") return isBrowser ? t("git.clone.progress.downloading") : t("git.clone.progress.wait");
	if (data.type === "finish") return t("git.clone.progress.finish");
	if (
		isBrowser &&
		(data.type === "sideband" ||
			data.type === "chunkedTransfer" ||
			data.type === "download" ||
			data.type === "download-no-progress")
	)
		return t("git.clone.progress.downloading");

	if (data.type === "checkout") return t("git.clone.progress.checkout");
	return "";
};

export type CloneProgressProps = {
	name: string;
	progress: RemoteProgressPercentage;
	isCancel?: boolean;
	setIsCancel?: (isCancel: boolean) => void;
	className?: string;
};

interface ProgressBlockProps {
	title: string;
	speed?: string;
	total?: string;
	indeterminate?: boolean;
	value?: number;
	onCancel?: () => void;
}

const ProgressBlock = ({ indeterminate, title, onCancel, value, speed, total }: ProgressBlockProps) => {
	return (
		<div className="flex w-full flex-col gap-1 max-w-full overflow-hidden">
			<div className="flex items-center gap-2 justify-between">
				<div className="flex items-center justify-between gap-1" style={{ maxWidth: "85%" }}>
					<div className="text-xs text-primary-fg font-normal whitespace-nowrap">{title}</div>
					{total && (
						<>
							{title && <Divider className="h-3" orientation="vertical" />}
							<div className="text-xs truncate text-primary-fg font-normal">{total}</div>
						</>
					)}
					{speed && (
						<>
							<Divider className="h-3" orientation="vertical" />
							<div className="text-xs truncate text-primary-fg font-normal">{speed}</div>
						</>
					)}
				</div>
				<div className="flex items-center w-full justify-end">
					{onCancel && (
						<IconButton
							icon="x"
							onClick={onCancel}
							size="sm"
							style={{ padding: "0", height: "auto" }}
							variant="text"
						/>
					)}
				</div>
			</div>
			<Progress indeterminate={indeterminate} size="sm" value={Math.min(value ?? 0, 100)} />
		</div>
	);
};

const CloneProgress = (props: CloneProgressProps) => {
	const { name, progress, isCancel, setIsCancel, className } = props;

	const { call: cancelClone } = useApi({
		url: (api) => api.cancelClone(name),
	});

	const { isBrowser } = usePlatform();

	const handleCancel = useCallback(async () => {
		if (!setIsCancel || isCancel || !progress?.cancellable) return;

		await cancelClone();
	}, [setIsCancel, cancelClone, isCancel, progress?.cancellable]);

	const tooltipContent = collectProgressInfo(progress);

	const title = resolveLabelText(progress, isBrowser);
	const total = !title ? formatCollectedBytes(progress) : undefined;
	const speed = isBrowser ? undefined : formatSpeed(progress);

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<div className={className} style={{ marginBottom: "-4px" }}>
					{isCancel ? (
						<ProgressBlock indeterminate title={t("git.clone.progress.cancel")} />
					) : (
						<ProgressBlock
							indeterminate={!progress?.percentage}
							onCancel={handleCancel}
							speed={speed}
							title={title}
							total={total}
							value={progress?.percentage}
						/>
					)}
				</div>
			</TooltipTrigger>
			{tooltipContent && (
				<TooltipContent>
					<div>{tooltipContent}</div>
				</TooltipContent>
			)}
		</Tooltip>
	);
};

export default styled(CloneProgress)`
	span {
		font-size: 11px !important;
	}

	width: 100%;
`;
